import React, { FC, Fragment, useState, ChangeEvent, useEffect, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
    Divider,
    Accordion,
    AccordionDetails,
    styled,
    useMediaQuery,
    Theme,
} from '@mui/material';
import { File as DenthubFile } from 'models/file';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { liveInternalOrderDetailsContext, refreshInternalOrderDetails } from 'apollo/order-live-updates';
import { linkInternalOrderDetails } from 'apollo/internal-order-details-cache';
import { useDraftState } from 'hooks/useDraftState';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getInternalOrderDetailsTemplate, notesTemplate, externalNotesTemplate } from 'apollo/templates/order';
import { translateOrderCommentsToEnglishTemplate } from 'apollo/templates/message';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { CONSTANTS } from 'config/constants';
import { hasRestorationAction } from 'utils/restoration-price-rows';
import ManageExternalTechnicians from 'components/shared/manage-external-users/manage-external-technicians.component';
import { customerSettings } from 'utils/customerSettings';
import { Order, OrderRejectStatus, OrderStatus } from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { markOrderInternalChatNotificationsAsReadTemplate } from 'apollo/templates/notification';
import MillingJobsForOrder from 'components/lab-internal-order-details/milling-jobs/milling-jobs-for-order.component';
import Chat from 'components/lab-internal-order-details/chat/chat.component';
import InternalFiles from 'components/lab-internal-order-details/internal-files.componen';
import ConstructionFiles from 'components/lab-internal-order-details/construction-files.component';
import { isSupplier } from 'utils/client-settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import ProductionValueReportWrapper from 'components/lab-internal-order-details/production-value-report-wrapper.component';
import OrderDetailsSchedulingSection from 'components/lab-internal-order-details/production-planning-section/order-details-scheduling-section.component';
import OrderInfoBox from 'components/lab-internal-order-details/order-info-box.component';
import WorkflowActions from 'components/shared/workflow-actions/workflow-actions.component';
import { LoadingButton } from '@mui/lab';

const defaultAccordionStates: { [key: string]: boolean } = {
    INTERNAL_NOTES: true,
    PRODUCTION_PLANNING: true,
    INTERNAL_FILES: true,
    PRODUCTION_VALUE_REPORT: true,
    CONSTRUCTION_FILES: true,
    MILLING_JOBS: true,
};

const collapsedAccordionStates: { [key: string]: boolean } = Object.fromEntries(
    Object.keys(defaultAccordionStates).map((key) => [key, false]),
);

import { useRefreshOrder } from 'hooks/useRefreshOrder';
type LabInternalOrderDetailsProps = {
    order: Order;
};
const LabInternalOrderDetails: FC<LabInternalOrderDetailsProps> = ({ order }: LabInternalOrderDetailsProps) => {
    const { t: translate } = useTranslation(['common', 'order-event', 'orders', 'milling-jobs', 'workflow']);
    // isInternalOrderData is derived server-side: re-read the order after a change (DD-3231).
    const refreshOrder = useRefreshOrder();
    const client = useApolloClient();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const translatedCommentsStorageKey = `translatedComments_${order._id}`;
    const [lastTranslated, setLastTranslated] = useState(localStorage.getItem(translatedCommentsStorageKey) ?? '');
    const [showExternalNotesSnackbar, setShowExternalNotesSnackbar] = useState(false);
    const [notesSnackbar, setNotesSnackbar] = useState(false);

    // Read from the cache and registered as a live view (DD-3231): a colleague's save reaches
    // this tab through INTERNAL_ORDER_DETAILS_CHANGED. The notes are drafts over that value,
    // so unsaved typing is never overwritten.
    const { loading, data } = useQuery(getInternalOrderDetailsTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        context: liveInternalOrderDetailsContext(order._id),
        variables: {
            orderId: order._id,
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const internalOrderDetails = data?.internalOrderDetails;
    const notes = useDraftState<string>(internalOrderDetails?.notes ?? '');
    const externalNotes = useDraftState<string>(internalOrderDetails?.externalNotes ?? '');
    const files: DenthubFile[] = internalOrderDetails?.signedUrls ?? [];
    const constructionFiles: DenthubFile[] = internalOrderDetails?.constructionsFilesUrls ?? [];
    const [accordionStates, setAccordionStates] = useState<{ [key: string]: boolean }>(() =>
        isMobile ? collapsedAccordionStates : defaultAccordionStates,
    );
    const { showBoundary } = useDenthubErrorBoundary();

    const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
        background: '#f1f1f1',
        borderBottom: '1px #eee solid',
    }));

    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );

    const isLabMillingEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_MILLING,
    );

    const isProductionValueReportEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_VALUE_REPORT,
    );

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );

    const showWorkflowActions =
        !!isLabWorkflowEnabled &&
        !order.isImportedOrder &&
        !!order.workFlow?.currentStep &&
        isSupplier(currentOrganization._id, order.supplier._id) &&
        order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB &&
        order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
        order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED;

    const hasAtLeastOneAction = hasRestorationAction(order?.restorations);

    const canViewProductionValueReport =
        isProductionValueReportEnabled &&
        isProductionValueReportEnabled &&
        isSupplier(currentOrganization._id, order.supplier._id) &&
        hasAtLeastOneAction;
    hasAtLeastOneAction;

    const [markOrderInternalChatNotificationsAsRead] = useMutation(markOrderInternalChatNotificationsAsReadTemplate, {
        onCompleted: () => {
            window.top?.postMessage(
                {
                    eventType: 'refresh-notifications',
                },
                '*',
            );
        },
    });

    const [saveNotes, { loading: notesLoading }] = useMutation(notesTemplate, {
        update(cache, { data }) {
            linkInternalOrderDetails(cache, data?.notes);
        },
        onCompleted: () => {
            setNotesSnackbar(true);
            refreshOrder(order._id);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [saveExternalNotes, { loading: externalNotesLoading }] = useMutation(externalNotesTemplate, {
        update(cache, { data }) {
            linkInternalOrderDetails(cache, data?.externalNotes);
        },
        onCompleted: () => {
            setShowExternalNotesSnackbar(true);
            refreshOrder(order._id);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        markOrderInternalChatNotificationsAsRead({
            variables: {
                orderId: order._id,
            },
        });
    }, []);

    const handleNotesClick = async () => {
        await saveNotes({
            variables: {
                labId: currentOrganization._id,
                orderId: order._id,
                notes: notes.value,
            },
        });
    };

    const handleExternalNotesClick = async () => {
        await saveExternalNotes({
            variables: {
                labId: currentOrganization._id,
                orderId: order._id,
                externalNotes: externalNotes.value,
            },
        });
    };

    const handleInternalFilesChange = () => {
        refreshOrder(order._id);
        refreshInternalOrderDetails(client, order._id);
    };

    const onNotesChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        notes.setValue(event.target.value);
    };

    const onExternalNotesChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        externalNotes.setValue(event.target.value);
        setShowExternalNotesSnackbar(false);
    };

    const [fetchTranslatedComments, { loading: translatingComments }] = useLazyQuery(
        translateOrderCommentsToEnglishTemplate,
        {
            fetchPolicy: 'no-cache',
            onCompleted: ({ translateOrderCommentsToEnglish: translatedText }) => {
                if (translatedText) {
                    externalNotes.setValue((current) => (current ? `${current}\n${translatedText}` : translatedText));
                    setLastTranslated(translatedText);
                    localStorage.setItem(translatedCommentsStorageKey, translatedText);
                }
            },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const handleImportAndTranslateComments = () => {
        fetchTranslatedComments({ variables: { orderId: order._id } });
    };

    const handleAccordionChange = (accordionName: string) => (_: SyntheticEvent, isExpanded: boolean) => {
        setAccordionStates((prevState: { [key: string]: boolean }) => {
            const value = {
                ...prevState,
                [accordionName]: isExpanded,
            };
            localStorage.setItem('internalOrderAccordionStates', JSON.stringify(value));
            return value;
        });
    };

    return (
        <Fragment>
            {notesSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_NOTES')} />
            ) : undefined}
            {showExternalNotesSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_EXTERNAL_NOTES')} />
            ) : undefined}
            {loading ? (
                <Loader />
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={9} xl={8}>
                        <Accordion
                            onChange={handleAccordionChange('INTERNAL_NOTES')}
                            expanded={accordionStates['INTERNAL_NOTES']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="internal-order-view.comments-accordion"
                            >
                                <Typography variant="h4">{translate('order-event:INTERNAL_NOTES')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box mb={1}>
                                    <TextField
                                        value={notes.value}
                                        onChange={onNotesChangeEvent}
                                        fullWidth
                                        id="outlined-textarea"
                                        multiline
                                        rows={12}
                                    />
                                </Box>
                                {!order.isImportedOrder && (
                                    <Box>
                                        <Button
                                            variant="contained"
                                            disabled={!notes.value || notesLoading}
                                            onClick={handleNotesClick}
                                        >
                                            {translate('order-event:SAVE_NOTES')}
                                        </Button>
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            onChange={handleAccordionChange('EXTERNAL_INSTRUCTIONS')}
                            expanded={accordionStates['EXTERNAL_INSTRUCTIONS']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="internal-order-view.comments-accordion"
                            >
                                <Typography variant="h4">{translate('order-event:EXTERNAL_INSTRUCTIONS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box mb={1}>
                                    <TextField
                                        value={externalNotes.value}
                                        onChange={onExternalNotesChangeEvent}
                                        fullWidth
                                        id="outlined-textarea"
                                        multiline
                                        rows={12}
                                    />
                                </Box>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    <LoadingButton
                                        id="import-and-translate-comments-button"
                                        loading={translatingComments}
                                        variant="outlined"
                                        disabled={!!lastTranslated && externalNotes.value.includes(lastTranslated)}
                                        onClick={handleImportAndTranslateComments}
                                    >
                                        {translate('order-event:IMPORT_AND_TRANSLATE_COMMENTS')}
                                    </LoadingButton>
                                    {!order.isImportedOrder && (
                                        <LoadingButton
                                            id="save-external-instruction-button"
                                            loading={externalNotesLoading}
                                            variant="contained"
                                            disabled={!externalNotes.value || !externalNotes.isDirty}
                                            onClick={handleExternalNotesClick}
                                        >
                                            {translate('order-event:SAVE_EXTERNAL_INSTRUCTION')}
                                        </LoadingButton>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {isProductionPlanningEnabled && isSupplier(currentOrganization._id, order.supplier._id) && (
                            <Accordion
                                onChange={handleAccordionChange('PRODUCTION_PLANNING')}
                                expanded={accordionStates['PRODUCTION_PLANNING']}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    data-test="internal-order-view.production-planning-accordion"
                                >
                                    <Typography variant="h4">{translate('orders:PRODUCTION_PLANNING')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <OrderDetailsSchedulingSection
                                        order={order}
                                        onOrderUpdate={() => refreshOrder(order._id)}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {canViewProductionValueReport && (
                            <Accordion
                                onChange={handleAccordionChange('PRODUCTION_VALUE_REPORT')}
                                expanded={accordionStates['PRODUCTION_VALUE_REPORT']}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    data-test="internal-order-view.production-value-report-accordion"
                                >
                                    <Typography variant="h4">{translate('orders:PRODUCTION_VALUE_REPORT')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ProductionValueReportWrapper />
                                </AccordionDetails>
                            </Accordion>
                        )}

                        <Accordion
                            onChange={handleAccordionChange('INTERNAL_FILES')}
                            expanded={accordionStates['INTERNAL_FILES']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="internal-order-view.internal-files-accordion"
                            >
                                <Typography variant="h4">{translate('order-event:INTERNAL_FILES')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <InternalFiles
                                    files={files}
                                    onChange={handleInternalFilesChange}
                                    orderId={order._id}
                                    isImportedOrder={order.isImportedOrder}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            onChange={handleAccordionChange('CONSTRUCTION_FILES')}
                            expanded={accordionStates['CONSTRUCTION_FILES']}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="internal-order-view.construction-files-accordion"
                            >
                                <Typography variant="h4">{translate('milling-jobs:CONSTRUCTION_FILES')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ConstructionFiles
                                    files={constructionFiles}
                                    onChange={handleInternalFilesChange}
                                    orderId={order._id}
                                    isImportedOrder={order.isImportedOrder}
                                />
                            </AccordionDetails>
                        </Accordion>

                        {isLabMillingEnabled && !order.isImportedOrder ? (
                            <Accordion
                                onChange={handleAccordionChange('MILLING_JOBS')}
                                expanded={accordionStates['MILLING_JOBS']}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    data-test="internal-order-view.milling-jobs-accordion"
                                >
                                    <Typography variant="h4">
                                        {translate('milling-jobs:MILLING_JOBS_FOR_THIS_ORDER')}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <MillingJobsForOrder
                                        orderDbId={order._id}
                                        orderId={order.orderId}
                                        internalOrderId={order.internalOrderId}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ) : undefined}
                    </Grid>
                    <Grid item xs={12} lg={3} xl={4}>
                        <OrderInfoBox order={order} />

                        <Box mb={2}>
                            <Chat orderId={order._id} isImportedOrder={order.isImportedOrder} />
                        </Box>

                        {showWorkflowActions && (
                            <Paper variant="outlined" square sx={{ mb: 2 }}>
                                <Box p={2}>
                                    <Typography variant="h3" mb={1}>
                                        {translate('workflow:WORKFLOW')}
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <WorkflowActions order={order} />
                                </Box>
                            </Paper>
                        )}

                        {isExternalLabUsersEnabled &&
                        isSupplier(currentOrganization._id, order.supplier._id) &&
                        !permissions.isExternalTechnician(currentUser?.role) &&
                        !order.isImportedOrder ? (
                            <Paper variant="outlined" square sx={{ marginBottom: 2 }}>
                                <Box p={2}>
                                    <Box mb={1}>
                                        <Typography variant="h3">
                                            {translate('orders:MANAGE_EXTERNAL_USERS')}
                                        </Typography>
                                    </Box>
                                    <Box mb={1}>
                                        <Divider />
                                    </Box>
                                    <ManageExternalTechnicians orderId={order._id} />
                                </Box>
                            </Paper>
                        ) : undefined}
                    </Grid>
                </Grid>
            )}
        </Fragment>
    );
};

export default LabInternalOrderDetails;
